/**
 * The look shared by the Reflections page and a reflection's page, taken from the lab page: a cover banner
 * (image or colour) with a back button, or the Otable logo on public pages, and section cards with a
 * coloured accent. Colours and the cover gradient come from the lab theme.
 */
import React from 'react';
import { Box, Container, Paper, Tooltip, Typography } from '@mui/material';
import BackButton from 'components/common/buttons/BackButton';
import otableLogo from 'media/otable_main.png';
import { coverBackground, labColor, tint } from 'components/collab/lab/labTheme';

export const pageColor = (page) => labColor(page);

// Public pages are read by people without the app's tiny root font size in mind
export const PUBLIC_TYPE_SCALE = {
  '& .MuiTypography-body1': { fontSize: '17px', lineHeight: 1.7 },
  '& .MuiTypography-body2': { fontSize: '15.5px', lineHeight: 1.6 },
  '& .MuiTypography-caption': { fontSize: '13.5px', lineHeight: 1.5 },
};

export function PageBackground({ page, publicMode, children }) {
  return (
    <Box sx={{ bgcolor: tint(pageColor(page), 0.06), minHeight: '100%', pb: 4, ...(publicMode && PUBLIC_TYPE_SCALE) }}>
      {children}
    </Box>
  );
}

/** The cover banner. `cover`: { cover_url, background_color }; children: the bottom of the banner. */
export function CoverHero({ cover, publicMode, minHeight = 260, children, onBack }) {
  return (
    <Box sx={{ background: coverBackground(cover), minHeight, display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ pt: 2, display: 'flex', alignItems: 'flex-start' }}>
        {publicMode ? <Box flex={1} /> : (
          <BackButton position="inline" onClick={onBack}
            sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.25)', '&:hover': { bgcolor: 'rgba(0,0,0,0.4)' } }} />
        )}
        {publicMode && (
          <Tooltip title="Made with Otable">
            <Box component="a" href="https://www.otable.net" target="_blank" rel="noopener noreferrer"
              sx={{ display: 'block', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 0.85 } }}>
              <Box component="img" src={otableLogo} alt="Otable"
                sx={{ height: 34, display: 'block',
                  filter: 'drop-shadow(-1px -1px 0 rgba(255,255,255,0.55)) drop-shadow(1px 1px 0 rgba(0,0,0,0.45)) drop-shadow(0 2px 3px rgba(0,0,0,0.25))' }} />
            </Box>
          </Tooltip>
        )}
      </Container>
      <Box flex={1} />
      <Container maxWidth="lg" sx={{ pb: 3, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}>{children}</Container>
    </Box>
  );
}

export function Section({ title, color, action, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2, ...sx }}>
      {title && (
        <Box display="flex" alignItems="center" mb={1.5}>
          <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: color, mr: 1.25 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', flex: 1 }}>{title}</Typography>
          {action}
        </Box>
      )}
      {children}
    </Paper>
  );
}

export function PublicFooter() {
  return (
    <Box component="footer" sx={{ mt: 5, pt: 2.5, pb: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">
        Published with <Box component="a" href="https://www.otable.net" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit' }}>Otable</Box>,
        where researchers find collaborators and funding.
      </Typography>
    </Box>
  );
}
